import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useOutletContext } from 'react-router';

import { Badge, SimplePopover, Text } from '@fly/core';
import type { RedirectReturnState } from '@fly/shared';
import { FlyOverlayLink, useIsInsideOverlayCard } from '@fly/shared-ui';
import { DeployType } from '@fly/store';

import type { EnvironmentOutletContext } from '../../containers/Environment/Environment';
import { buildDeploymentWaveRegistryReleasesHref } from '../../utils/deploymentWaveRegistryReleasesHref';

/** Release wave row used to scope the "Release delta" deep-link to a single deployment. */
export type DeploymentReleaseDeltaIntermediate = {
  workflowName: string;
  workflowNumber: string;
  title?: string | null;
  shortSummary?: string | null;
  timestamp: string;
  url?: string | null;
};

export type DeploymentReleaseDeltaProps = {
  deployNumber: number;
  repositoryOwner?: string | null;
  repositoryName?: string | null;
  /** Prompt context for the registry deep-link; falls back to the resolved environment from the env-detail route's Outlet context. */
  environmentName?: string;
  /**
   * Opaque `DeploymentDelta.id` for the Releases page `where.deploymentId`
   * deep link. Preferred over the workflow triple when set.
   */
  deploymentId?: string;
  /**
   * Workflow name of the current deployment (= `EnvironmentRelease.name`).
   * Together with `deploymentWorkflowNumber` and the resolved environment name
   * forms the `DeploymentRefInput` triple that scopes the Releases page to
   * the full wave (legacy URL path when `deploymentId` is absent). When any
   * of the three is missing the link degrades to the env-only prompt.
   */
  deploymentWorkflowName?: string;
  /** Workflow run number of the current deployment (= `EnvironmentRelease.number`). */
  deploymentWorkflowNumber?: string;
  /**
   * AI-generated cumulative deployment headline (`DeploymentDelta.cumulativeTitle`).
   * Used for the Releases page filter chip label; not the single release title.
   */
  deploymentTitle?: string | null;
  /**
   * The release whose deployment landed this wave. Required for the button
   * to be clickable — without it the row collapses to a read-only badge.
   */
  deployedRelease?: DeploymentReleaseDeltaIntermediate | null;
  /**
   * Wave classification from the backend. Switches the button label and popover
   * header between forward ("Release delta") and rollback framing.
   * Defaults to the forward framing when absent.
   */
  deployType?: DeployType;
};

export function DeploymentReleaseDelta({
  deployNumber,
  repositoryOwner,
  repositoryName,
  environmentName: environmentNameProp,
  deploymentId,
  deploymentWorkflowName,
  deploymentWorkflowNumber,
  deploymentTitle,
  deployedRelease,
  deployType,
}: DeploymentReleaseDeltaProps) {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const outletCtx = useOutletContext<EnvironmentOutletContext | undefined>();
  // Inside an overlay card the trigger must be a hoisted anchor (`FlyOverlayLink`),
  // otherwise it would be an invalid `<a>` nested in the card's base link. Outside
  // (e.g. the env-detail page) the classic hover popover + plain link is kept.
  const inOverlayCard = useIsInsideOverlayCard();

  const environmentNameForLinks =
    environmentNameProp ?? outletCtx?.environment.name;

  const owner = repositoryOwner?.trim() ?? '';
  const repo = repositoryName?.trim() ?? '';

  const deployment =
    environmentNameForLinks &&
    deploymentWorkflowName &&
    deploymentWorkflowNumber
      ? {
          environmentName: environmentNameForLinks,
          workflowName: deploymentWorkflowName,
          workflowNumber: deploymentWorkflowNumber,
        }
      : undefined;

  const envNameFromWorkflow =
    deploymentWorkflowName && deploymentWorkflowNumber
      ? `${deploymentWorkflowName} #${deploymentWorkflowNumber}`
      : deploymentWorkflowName || environmentNameForLinks || '';

  const envNameForRegistryPrompt =
    deploymentTitle?.trim() || envNameFromWorkflow;
  const isRollback = deployType === DeployType.Rollback;
  const popoverHeader = isRollback ? (
    <div className='min-w-0 flex flex-col'>
      <Text size='2' color='neutral'>
        {t(
          'app.dashboard.runtime_environments.deployments.release_delta_popover_header_rollback_label',
          {
            defaultValue: 'Release delta since last deployment',
          }
        )}
      </Text>
      <Text size='2' color='neutral'>
        {t(
          'app.dashboard.runtime_environments.deployments.release_delta_popover_header_rollback_detail',
          {
            count: deployNumber,
            defaultValue: 'Rolled back (-{{count}}) releases',
          }
        )}
      </Text>
    </div>
  ) : (
    <div className='min-w-0 flex flex-col'>
      <Text size='2' color='neutral'>
        {t(
          'app.dashboard.runtime_environments.deployments.release_delta_popover_header_label',
          {
            defaultValue: 'Release delta since last deployment',
          }
        )}
      </Text>
      <Text size='2' color='neutral'>
        {t(
          'app.dashboard.runtime_environments.deployments.release_delta_popover_header_count',
          {
            count: deployNumber,
            defaultValue: '({{count}})',
          }
        )}
      </Text>
    </div>
  );

  const label = isRollback
    ? t(
        'app.dashboard.runtime_environments.deployments.release_delta_button_rollback',
        {
          count: deployNumber,
          defaultValue: 'Release delta (-{{count}})',
        }
      )
    : t('app.dashboard.runtime_environments.deployments.release_delta_button', {
        deploy_number: deployNumber,
        defaultValue: 'Release delta ({{deploy_number}})',
      });

  const href = buildDeploymentWaveRegistryReleasesHref(owner, repo, {
    displayName: envNameForRegistryPrompt,
    deployCount: deployNumber,
    isRollback,
    ...(deploymentId?.trim()
      ? { deploymentId: deploymentId.trim() }
      : { deployment }),
  });

  const isClickable = Boolean(owner && repo && deployedRelease);

  const redirectTo = useMemo((): RedirectReturnState | undefined => {
    if (!isClickable) {
      return undefined;
    }
    return {
      label: t('app.dashboard.runtime_environments.deployments.title', {
        defaultValue: 'Deployments',
      }),
      path: `${pathname}${search}`,
    };
  }, [isClickable, pathname, search, t]);

  const clickableBadge = (
    <Badge
      size='1'
      variant='soft'
      color={deployType === DeployType.Rollback ? 'warning' : 'neutral'}
      iconStart='release'
      hoverable
    >
      {label}
    </Badge>
  );

  const popoverRootProps = { openOnHover: true } as const;
  const popoverContentProps = {
    header: popoverHeader,
    align: 'center',
    variant: 'classic',
    color: 'neutral',
  } as const;

  // Inside an overlay card: hoist the anchor out of the card's base link via
  // `FlyOverlayLink` (no nested `<a>`, clicks don't bubble to the card link).
  // The popover must wrap the VISIBLE hoisted anchor — not the in-flow twin — so
  // it is applied through `wrapAnchor`. That way `SimplePopover`'s hover trigger
  // binds to the portaled, interactive anchor where `:hover` actually fires.
  if (inOverlayCard && isClickable) {
    return (
      <FlyOverlayLink
        to={href}
        state={redirectTo ? { redirectTo } : undefined}
        aria-label={label}
        linkClassName='min-w-0 decoration-none outline-none focus-visible:ring-2 focus-visible:ring-(--accent-8)'
        data-testid='deployment-release-delta-button'
        wrapAnchor={(anchor) => (
          <SimplePopover
            trigger={anchor}
            rootProps={popoverRootProps}
            contentProps={popoverContentProps}
          />
        )}
      >
        {clickableBadge}
      </FlyOverlayLink>
    );
  }

  const trigger = isClickable ? (
    <Link
      to={href}
      state={redirectTo ? { redirectTo } : undefined}
      className='min-w-0 decoration-none inline-block outline-none focus-visible:ring-2 focus-visible:ring-(--accent-8)'
      data-testid='deployment-release-delta-button'
    >
      {clickableBadge}
    </Link>
  ) : (
    <Badge
      size='1'
      variant='soft'
      color='neutral'
      iconStart='release'
      data-testid='deployment-release-delta-badge'
    >
      {label}
    </Badge>
  );

  return (
    <SimplePopover
      trigger={trigger}
      rootProps={popoverRootProps}
      contentProps={popoverContentProps}
    />
  );
}
